import { useEffect, useState } from 'react'
import { Card, Chip, EmptyState, LoadingRows, shortDate } from '../family-finance/shared'
import type { DraftBoard, DraftSlot } from '../../types/fantasy-football'
import { generateDraftOrder } from '../../lib/fantasy-analytics'
import {
  boardRows,
  buildCsv,
  buildPlainList,
  copyToClipboard,
  downloadCsv,
  draftBoardFilename,
} from '../../lib/draft-export'
import DraftCheatSheet from './DraftCheatSheet'
import {
  approveDraftBoard,
  getDraftBoard,
  saveProposedDraftBoard,
  updateDraftBoardSlots,
} from '../../lib/fantasy-api'
import type { FantasyState } from './useFantasy'
import { fmtPts, POSITION_TONES } from './useFantasy'

const exportBtn =
  'px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-700 ' +
  'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors'

export default function DraftTab({ state }: { state: FantasyState }) {
  const { loading, players, engineInput } = state
  const [board, setBoard] = useState<DraftBoard | null>(null)
  const [boardLoading, setBoardLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [view, setView] = useState<'list' | 'sheet'>('list')
  const [copyState, setCopyState] = useState<'idle' | 'ok' | 'fail'>('idle')

  useEffect(() => {
    let cancelled = false
    getDraftBoard().then((b) => {
      if (!cancelled) {
        setBoard(b)
        setBoardLoading(false)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  const playerById = new Map(players.map((p) => [p.id, p]))

  async function handleGenerate() {
    if (!engineInput) return
    setBusy('generate')
    try {
      // F12 runs client-side against current prefs/params, then persists as
      // a proposal — it only becomes the active order on explicit approval.
      const slots = generateDraftOrder(engineInput, 60)
      const saved = await saveProposedDraftBoard(slots)
      setBoard(saved)
    } finally {
      setBusy(null)
    }
  }

  async function handleApprove() {
    setBusy('approve')
    try {
      setBoard(await approveDraftBoard())
    } finally {
      setBusy(null)
    }
  }

  // Yahoo has no API for pre-draft rankings — fspt-w covers lineups, add/drops,
  // waivers and trades only — so the board leaves the app as a file or as text
  // you paste into Yahoo's pre-rank page by hand.
  const rows = board ? boardRows(board, players) : []

  function handleDownloadCsv() {
    if (!board) return
    downloadCsv(draftBoardFilename(board), buildCsv(rows))
  }

  async function handleCopyList() {
    try {
      await copyToClipboard(buildPlainList(rows))
      setCopyState('ok')
    } catch {
      // Browsers can refuse the clipboard outright; say so rather than
      // flashing a success the clipboard does not back up.
      setCopyState('fail')
    }
    setTimeout(() => setCopyState('idle'), 2500)
  }

  async function move(index: number, dir: -1 | 1) {
    if (!board) return
    const target = index + dir
    if (target < 0 || target >= board.slots.length) return
    const slots: DraftSlot[] = [...board.slots]
    ;[slots[index], slots[target]] = [slots[target], slots[index]]
    slots.forEach((s, i) => (s.overallRank = i + 1))
    setBoard({ ...board, slots, status: 'proposed', approvedAt: null })
    await updateDraftBoardSlots(slots)
  }

  if (loading || boardLoading) return <LoadingRows rows={8} />

  return (
    <div className="space-y-6">
      <Card
        title="Draft order"
        subtitle="Generated by greedy roster simulation (F12): each simulated pick updates covariance, bye penalties, and positional need for the next. Reorder freely — the board only becomes active when you approve it."
        action={
          <div className="flex items-center gap-2">
            {board?.status === 'approved' && board.approvedAt && (
              <Chip tone="green">Approved {shortDate(board.approvedAt)}</Chip>
            )}
            {board?.status === 'proposed' && <Chip tone="amber">Awaiting your approval</Chip>}
            <button
              onClick={handleGenerate}
              disabled={busy !== null || !engineInput}
              className="px-3 py-1.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50 text-slate-800 dark:text-slate-200 text-sm rounded-lg transition-colors"
            >
              {busy === 'generate' ? 'Simulating…' : board ? 'Regenerate' : 'Generate draft order'}
            </button>
            {board?.status === 'proposed' && (
              <button
                onClick={handleApprove}
                disabled={busy !== null}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {busy === 'approve' ? 'Approving…' : 'Approve order'}
              </button>
            )}
            {board && (
              <div className="flex items-center gap-1 pl-2 ml-1 border-l border-slate-300 dark:border-slate-700 print:hidden">
                <button onClick={handleDownloadCsv} className={exportBtn} title="CSV — download the board as a spreadsheet">
                  CSV
                </button>
                <button onClick={handleCopyList} className={exportBtn} title="Copy — ordered list for pasting into Yahoo's pre-rank page">
                  {copyState === 'ok' ? 'Copied' : copyState === 'fail' ? 'Blocked' : 'Copy'}
                </button>
                <button
                  onClick={() => setView(view === 'sheet' ? 'list' : 'sheet')}
                  className={exportBtn}
                  title={
                    view === 'sheet'
                      ? 'List — back to the ranked draft order'
                      : 'Cheat sheet — position-grouped tier view for draft day'
                  }
                >
                  {view === 'sheet' ? 'List' : 'Cheat sheet'}
                </button>
                {view === 'sheet' && (
                  <button onClick={() => window.print()} className={exportBtn} title="Print the cheat sheet">
                    Print
                  </button>
                )}
              </div>
            )}
          </div>
        }
      >
        {!board ? (
          <EmptyState
            icon="📋"
            message="No draft order yet. Generate one from your current risk preferences and formula parameters."
          />
        ) : view === 'sheet' ? (
          <DraftCheatSheet rows={rows} />
        ) : (
          <div className="space-y-1 print:hidden">
            {board.slots.map((slot, index) => {
              const player = playerById.get(slot.playerId)
              if (!player) return null
              return (
                <div
                  key={slot.playerId}
                  className="flex items-center gap-3 bg-slate-100/60 dark:bg-slate-900/60 rounded-lg px-3 py-2"
                >
                  <span className="text-slate-500 text-sm w-8 shrink-0">{slot.overallRank}</span>
                  <Chip tone={POSITION_TONES[player.position]}>{player.position}</Chip>
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-900 dark:text-white text-sm font-medium truncate">
                      {player.name}
                      <span className="text-slate-500 ml-2 text-xs">
                        {player.nflTeam} · Bye {player.byeWeek}
                      </span>
                    </p>
                    <p className="text-slate-500 text-xs truncate">{slot.reason}</p>
                  </div>
                  <span className="text-slate-600 dark:text-slate-300 text-sm w-14 text-right shrink-0">
                    {fmtPts(slot.score, 1)}
                  </span>
                  <div className="flex flex-col shrink-0">
                    <button
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      className="text-slate-500 hover:text-slate-900 dark:hover:text-white disabled:opacity-30 leading-none px-1"
                      aria-label="Move up"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => move(index, 1)}
                      disabled={index === board.slots.length - 1}
                      className="text-slate-500 hover:text-slate-900 dark:hover:text-white disabled:opacity-30 leading-none px-1"
                      aria-label="Move down"
                    >
                      ▼
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>

      <Card title="How this board is built">
        <ol className="text-slate-500 dark:text-slate-400 text-sm space-y-2 list-decimal list-inside">
          <li>Every player gets a scarcity-weighted risk-adjusted value (F1–F9) against the simulated roster so far.</li>
          <li>A positional-need multiplier (F12) discounts positions whose starting slots are already filled, and dampens K/DST until the core is set.</li>
          <li>The top-scoring player is "picked", joins the simulated roster, and the next slot re-prices — so the board itself avoids stacking correlated byes and teammates you did not opt into.</li>
          <li>Each slot records its score and drivers; the full parameter snapshot is written to the audit log when you generate and when you approve.</li>
        </ol>
      </Card>
    </div>
  )
}
